import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Check, Copy, Download, FileText, Lock, Mail, MessageCircle, Save, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import JaceLayout from "./JaceLayout";
import { JACE, TYPE_LABELS, fmtDateNL } from "./shared";

const NOTE_FIELDS = [
  ["free_text", "Vrije observaties"],
  ["strengths", "Technische sterke punten"],
  ["improvements", "Technische verbeterpunten"],
  ["posture", "Houding"],
  ["coordination", "Coördinatie"],
  ["rhythm", "Ritme"],
  ["conditioning", "Conditie"],
  ["personal_focus", "Persoonlijke aandachtspunten"],
];
const REPORT_SECTIONS = [
  ["overall", "Algemene indruk"],
  ["went_well", "Wat ging goed?"],
  ["improvements", "Technische verbeterpunten"],
  ["focus", "Aanbevolen trainingsfocus"],
  ["conclusion", "Conclusie van de coach"],
];

const emptyNotes = () => Object.fromEntries(NOTE_FIELDS.map(([key]) => [key, ""]));
const phoneForWhatsApp = (phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  if (digits.startsWith("06")) return `31${digits.slice(1)}`;
  return digits.replace(/^00/, "");
};
const reportText = (report) => REPORT_SECTIONS.map(([key, label]) => `${label}\n${report?.sections?.[key] || ""}`).filter((x) => x.trim()).join("\n\n");

export default function JaceFeedbackEditor() {
  const { feedbackId } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [settings, setSettings] = useState(null);
  const [session, setSession] = useState(null);
  const [doc, setDoc] = useState(null);
  const [meta, setMeta] = useState({ athlete_name: "", athlete_email: "", athlete_phone: "", training_date: "" });
  const [athleteId, setAthleteId] = useState("");
  const [notes, setNotes] = useState(emptyNotes());
  const [custom, setCustom] = useState({});
  const [blockFeedback, setBlockFeedback] = useState([]);
  const [include, setInclude] = useState({ custom_questions: [], blocks: [] });
  const [report, setReport] = useState(null);
  const [templateId, setTemplateId] = useState("standard");
  const [busy, setBusy] = useState("");
  const [shareMode, setShareMode] = useState(null);
  const [manualTarget, setManualTarget] = useState("");

  const isNew = !feedbackId;

  useEffect(() => {
    api.get("/jace/feedback/settings").then(({ data }) => { setSettings(data); setTemplateId(data.templates?.[0]?.id || "standard"); }).catch(() => setSettings({ questions: [], templates: [] }));
    if (feedbackId) {
      api.get(`/jace/feedback/${feedbackId}`).then(({ data }) => hydrate(data)).catch(() => setDoc(false));
    } else if (params.get("session")) {
      api.get(`/jace/sessions/${params.get("session")}`).then(({ data }) => {
        setSession(data);
        setAthleteId(data.athlete_id || "");
        setMeta({ athlete_name: data.athlete || "", athlete_email: "", athlete_phone: "", training_date: data.date || "" });
        setBlockFeedback((data.blocks || []).map((b) => ({ block_id: b.id, block_name: b.name, question_id: "general", note: "", rating: null })));
        setDoc({ id: "", status: "draft" });
      }).catch(() => setDoc(false));
    } else if (params.get("athlete")) {
      api.get(`/jace/athletes/${params.get("athlete")}`).then(({ data }) => {
        setAthleteId(data.id);
        setMeta({ athlete_name: data.name || "", athlete_email: data.email || "", athlete_phone: data.phone || "", training_date: new Date().toISOString().slice(0, 10) });
        setDoc({ id: "", status: "draft", training_blocks: [] });
      }).catch(() => setDoc(false));
    } else {
      setDoc({ id: "", status: "draft", training_blocks: [] });
    }
  }, [feedbackId]); // eslint-disable-line react-hooks/exhaustive-deps

  const hydrate = (data) => {
    setDoc(data);
    setAthleteId(data.athlete_id || "");
    setMeta({ athlete_name: data.athlete_name || "", athlete_email: data.athlete_email || "", athlete_phone: data.athlete_phone || "", training_date: data.training_date || "" });
    setNotes({ ...emptyNotes(), ...(data.coach_notes || {}) });
    setCustom(data.custom_answers || {});
    setBlockFeedback(data.block_feedback || []);
    setInclude(data.share_selection || { custom_questions: [], blocks: [] });
    setReport(data.report || null);
    setTemplateId(data.report?.template_id || "standard");
  };

  const blocks = doc?.training_blocks || session?.blocks?.map((b) => ({ block_id: b.id, block_name: b.name })) || [];
  const activeQuestions = useMemo(() => (settings?.questions || []).filter((q) => q.active !== false), [settings]);
  const selectedCount = NOTE_FIELDS.filter(([key]) => include[key] && notes[key]).length + (include.custom_questions || []).length + (include.blocks || []).length;

  const setBlockEntry = (block, questionId, patch) => {
    setBlockFeedback((prev) => {
      const found = prev.find((b) => b.block_id === (block.block_id || block.id) && (b.question_id || "general") === questionId);
      if (found) return prev.map((b) => b === found ? { ...b, ...patch } : b);
      return [...prev, { block_id: block.block_id || block.id, block_name: block.block_name || block.name, question_id: questionId, note: "", rating: null, ...patch }];
    });
  };
  const blockEntry = (block, questionId = "general") => blockFeedback.find((b) => b.block_id === (block.block_id || block.id) && (b.question_id || "general") === questionId) || {};

  const save = async (finalize = false) => {
    if (!meta.athlete_name.trim()) { toast.error("Vul de atleetnaam in"); return null; }
    setBusy(finalize ? "finalize" : "save");
    try {
      const payload = { ...meta, athlete_id: athleteId, session_id: session?.session_id || doc?.session_id || "", coach_notes: notes, custom_answers: custom, block_feedback: blockFeedback, share_selection: include };
      let data;
      if (doc?.id) {
        ({ data } = await api.put(`/jace/feedback/${doc.id}`, payload));
      } else {
        ({ data } = await api.post("/jace/feedback", payload));
        data = data.feedback;
        nav(`/jace/feedback/${data.id}`, { replace: true });
      }
      if (finalize) await api.post(`/jace/feedback/${data.id}/finalize`);
      toast.success(finalize ? "Feedback definitief opgeslagen" : "Concept opgeslagen");
      const refreshed = await api.get(`/jace/feedback/${data.id}`);
      hydrate(refreshed.data);
      return refreshed.data;
    } catch (err) { toast.error(err?.response?.data?.detail || "Opslaan mislukt"); return null; }
    finally { setBusy(""); }
  };

  const generate = async () => {
    const saved = doc?.id ? doc : await save(false);
    if (!saved?.id) return;
    if (selectedCount === 0) { toast.error("Selecteer eerst welke coach notes JACE mag gebruiken"); return; }
    setBusy("generate");
    try {
      const { data } = await api.post(`/jace/feedback/${saved.id}/generate-report`, { template_id: templateId, include });
      hydrate(data);
      toast.success("Concept Athlete Report gegenereerd — controleer en keur secties goed");
    } catch (err) { toast.error(err?.response?.data?.detail || "Rapport genereren mislukt"); }
    setBusy("");
  };

  const saveReport = async (finalize = false) => {
    if (!doc?.id || !report) return;
    setBusy(finalize ? "finalize-report" : "save-report");
    try {
      await api.put(`/jace/feedback/${doc.id}/report`, { sections: report.sections, approved_sections: report.approved_sections || [] });
      if (finalize) await api.post(`/jace/feedback/${doc.id}/report/finalize`);
      const { data } = await api.get(`/jace/feedback/${doc.id}`);
      hydrate(data);
      toast.success(finalize ? "Rapport definitief afgerond" : "Rapport opgeslagen");
    } catch (err) { toast.error(err?.response?.data?.detail || "Rapport opslaan mislukt"); }
    setBusy("");
  };

  const copyReport = async () => {
    const text = reportText(report);
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Rapporttekst gekopieerd");
    } catch {
      try {
        const area = document.createElement("textarea");
        area.value = text;
        area.setAttribute("readonly", "");
        area.style.position = "fixed";
        area.style.left = "-9999px";
        document.body.appendChild(area);
        area.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(area);
        if (!ok) throw new Error("copy failed");
        toast.success("Rapporttekst gekopieerd");
      } catch {
        toast.error("Kopiëren mislukt — selecteer de rapporttekst handmatig");
      }
    }
  };
  const downloadPdf = async () => {
    try {
      const res = await api.get(`/jace/feedback/${doc.id}/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `jace-report-${(meta.athlete_name || "athlete").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(err?.response?.data?.detail || "PDF downloaden mislukt"); }
  };
  const shareWhatsApp = (target = meta.athlete_phone) => {
    const phone = phoneForWhatsApp(target);
    const url = phone ? `https://wa.me/${phone}?text=${encodeURIComponent(reportText(report))}` : `https://wa.me/?text=${encodeURIComponent(reportText(report))}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setShareMode(null);
  };
  const shareEmail = (target = meta.athlete_email) => {
    if (!target) { setShareMode("email"); return; }
    window.location.href = `mailto:${encodeURIComponent(target)}?subject=${encodeURIComponent(`JACE Athlete Report — ${meta.athlete_name}`)}&body=${encodeURIComponent(reportText(report))}`;
    setShareMode(null);
  };
  const nativeShare = async () => {
    if (navigator.share) await navigator.share({ title: `JACE Athlete Report — ${meta.athlete_name}`, text: reportText(report) });
    else shareWhatsApp("");
  };

  if (doc === null || settings === null) return <JaceLayout><div className="chip text-[#A3A3A3]">Laden…</div></JaceLayout>;
  if (doc === false) return <JaceLayout><div className="border border-[#F5F3F0]/15 p-10 text-center chip text-[#4A4A4A]">Feedback niet gevonden.</div></JaceLayout>;

  return (
    <JaceLayout>
      <div className="max-w-6xl" data-testid="jace-feedback-editor">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <div className="chip mb-2" style={{ color: JACE }}>// {isNew ? "NEW FEEDBACK" : `FEEDBACK — ${doc.status?.toUpperCase()}`}</div>
            <h2 className="font-display uppercase text-4xl md:text-6xl">{meta.athlete_name || "Nieuwe evaluatie"}.</h2>
            <div className="chip text-[#A3A3A3] mt-2">{session ? `${session.session_id} · ${TYPE_LABELS[session.type]} · ${fmtDateNL(session.date)}` : (doc.session_title || "Handmatige evaluatie")}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => save(false)} disabled={!!busy} className="btn-ghost-dark inline-flex items-center gap-2" data-testid="jace-feedback-save"><Save size={13}/> {busy === "save" ? "OPSLAGEN…" : "SAVE DRAFT"}</button>
            <button onClick={() => save(true)} disabled={!!busy} className="chip px-4 py-2.5 border inline-flex items-center gap-2" style={{ borderColor: JACE, color: JACE }} data-testid="jace-feedback-finalize"><Check size={13}/> FINALIZE NOTES</button>
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_380px] gap-8">
          <div>
            <section className="border border-[#F5F3F0]/15 p-5 mb-8" data-testid="jace-feedback-athlete-panel">
              <div className="chip mb-4" style={{ color: JACE }}>ATHLETE & SESSION</div>
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className="chip text-[#A3A3A3]">Atleet*</label><input value={meta.athlete_name} onChange={(e) => setMeta((p) => ({ ...p, athlete_name: e.target.value }))} className="editorial-input dark" data-testid="jace-feedback-athlete"/></div>
                <div><label className="chip text-[#A3A3A3]">Trainingsdatum</label><input type="date" value={meta.training_date} onChange={(e) => setMeta((p) => ({ ...p, training_date: e.target.value }))} className="editorial-input dark" data-testid="jace-feedback-date"/></div>
                <div><label className="chip text-[#A3A3A3]">E-mail atleet</label><input type="email" value={meta.athlete_email} onChange={(e) => setMeta((p) => ({ ...p, athlete_email: e.target.value }))} className="editorial-input dark" data-testid="jace-feedback-email"/></div>
                <div><label className="chip text-[#A3A3A3]">Telefoon atleet</label><input value={meta.athlete_phone} onChange={(e) => setMeta((p) => ({ ...p, athlete_phone: e.target.value }))} className="editorial-input dark" data-testid="jace-feedback-phone"/></div>
              </div>
              {blocks.length > 0 && <div className="chip text-[#4A4A4A] mt-4">Onderdelen: {blocks.map((b) => b.block_name || b.name).join(" · ")}</div>}
            </section>

            <section className="border border-[#F5F3F0]/15 p-5 mb-8" data-testid="jace-coach-notes">
              <div className="flex items-center gap-2 mb-4"><Lock size={14} style={{ color: JACE }}/><div className="chip" style={{ color: JACE }}>COACH NOTES — PRIVÉ</div></div>
              <div className="space-y-5">
                {NOTE_FIELDS.map(([key, label]) => (
                  <div key={key}>
                    <label className="chip text-[#A3A3A3]">{label}</label>
                    <textarea rows={key === "free_text" ? 4 : 2} value={notes[key]} onChange={(e) => setNotes((p) => ({ ...p, [key]: e.target.value }))} className="editorial-input dark w-full" data-testid={`jace-note-${key}`}/>
                  </div>
                ))}
              </div>
            </section>

            {activeQuestions.length > 0 && (
              <section className="border border-[#F5F3F0]/15 p-5 mb-8" data-testid="jace-feedback-custom-questions">
                <div className="chip mb-4" style={{ color: JACE }}>CUSTOM QUESTIONS</div>
                <div className="space-y-5">
                  {activeQuestions.map((q) => (
                    <div key={q.id} data-testid={`jace-custom-${q.id}`}>
                      <label className="chip text-[#A3A3A3]">{q.label}</label>
                      {q.type === "rating" ? (
                        <div className="flex gap-2">{[1, 2, 3, 4, 5].map((n) => <button key={n} onClick={() => setCustom((p) => ({ ...p, [q.id]: n }))} className="w-10 h-10 border font-mono" style={custom[q.id] === n ? { background: JACE, color: "#0A0A0A", borderColor: JACE } : { borderColor: "#F5F3F033", color: "#A3A3A3" }} data-testid={`jace-custom-${q.id}-${n}`}>{n}</button>)}</div>
                      ) : q.type === "block" ? (
                        <div className="space-y-3">{blocks.map((b) => {
                          const entry = blockEntry(b, q.id);
                          return <div key={`${q.id}-${b.block_id || b.id}`}><div className="chip text-[#4A4A4A] mb-1">{b.block_name || b.name}</div><textarea rows={2} value={entry.note || ""} onChange={(e) => setBlockEntry(b, q.id, { note: e.target.value })} className="editorial-input dark w-full" data-testid={`jace-custom-${q.id}-${b.block_id || b.id}`}/></div>;
                        })}</div>
                      ) : (
                        <textarea rows={2} value={custom[q.id] || ""} onChange={(e) => setCustom((p) => ({ ...p, [q.id]: e.target.value }))} className="editorial-input dark w-full" data-testid={`jace-custom-answer-${q.id}`}/>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {blocks.length > 0 && (
              <section className="border border-[#F5F3F0]/15 p-5 mb-8" data-testid="jace-block-feedback">
                <div className="chip mb-4" style={{ color: JACE }}>FEEDBACK PER TRAININGSONDERDEEL</div>
                {blocks.map((b, i) => {
                  const entry = blockEntry(b);
                  return (
                    <div key={b.block_id || b.id || i} className="border-b border-[#F5F3F0]/10 py-4" data-testid={`jace-block-feedback-${b.block_id || b.id || i}`}>
                      <div className="flex flex-wrap items-center gap-3 mb-2"><span className="font-display uppercase text-lg">{b.block_name || b.name}</span><div className="flex gap-1">{[1, 2, 3, 4, 5].map((n) => <button key={n} onClick={() => setBlockEntry(b, "general", { rating: n })} className="w-8 h-8 border font-mono text-xs" style={entry.rating === n ? { background: JACE, color: "#0A0A0A", borderColor: JACE } : { borderColor: "#F5F3F033", color: "#A3A3A3" }}>{n}</button>)}</div></div>
                      <textarea rows={2} value={entry.note || ""} onChange={(e) => setBlockEntry(b, "general", { note: e.target.value })} className="editorial-input dark w-full" data-testid={`jace-block-note-${b.block_id || b.id || i}`}/>
                    </div>
                  );
                })}
              </section>
            )}
          </div>

          <aside className="space-y-6">
            <section className="border border-[#F5F3F0]/15 p-5" data-testid="jace-report-selection">
              <div className="chip mb-2" style={{ color: JACE }}>SELECT FOR REPORT</div>
              <p className="text-xs text-[#A3A3A3] mb-4">Privé notes worden nooit automatisch gedeeld. Selecteer expliciet wat JACE mag gebruiken.</p>
              {NOTE_FIELDS.map(([key, label]) => (
                <label key={key} className="flex items-start gap-3 py-2 chip text-[#DDD6CB]" data-testid={`jace-include-${key}`}>
                  <input type="checkbox" checked={!!include[key]} onChange={(e) => setInclude((p) => ({ ...p, [key]: e.target.checked }))} className="mt-1 accent-[#C6FF3C]"/>
                  <span>{label}{notes[key] ? "" : " — leeg"}</span>
                </label>
              ))}
              {activeQuestions.filter((q) => q.shareable !== false).map((q) => (
                <label key={q.id} className="flex items-start gap-3 py-2 chip text-[#DDD6CB]" data-testid={`jace-include-custom-${q.id}`}>
                  <input type="checkbox" checked={(include.custom_questions || []).includes(q.id)} onChange={(e) => setInclude((p) => ({ ...p, custom_questions: e.target.checked ? [...(p.custom_questions || []), q.id] : (p.custom_questions || []).filter((x) => x !== q.id) }))} className="mt-1 accent-[#C6FF3C]"/>
                  <span>{q.label}</span>
                </label>
              ))}
              {blocks.map((b, i) => (
                <label key={b.block_id || b.id || i} className="flex items-start gap-3 py-2 chip text-[#DDD6CB]" data-testid={`jace-include-block-${b.block_id || b.id || i}`}>
                  <input type="checkbox" checked={(include.blocks || []).includes(b.block_id || b.id)} onChange={(e) => setInclude((p) => ({ ...p, blocks: e.target.checked ? [...(p.blocks || []), b.block_id || b.id] : (p.blocks || []).filter((x) => x !== (b.block_id || b.id)) }))} className="mt-1 accent-[#C6FF3C]"/>
                  <span>Onderdeel: {b.block_name || b.name}</span>
                </label>
              ))}
              <div className="mt-5">
                <label className="chip text-[#A3A3A3]">Rapport template</label>
                <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} className="editorial-input dark" data-testid="jace-report-template">
                  {(settings.templates || []).map((t) => <option key={t.id} value={t.id} className="bg-[#0A0A0A]">{t.name}</option>)}
                </select>
              </div>
              <button onClick={generate} disabled={!!busy || selectedCount === 0} className="w-full mt-5 py-3 chip inline-flex items-center justify-center gap-2 disabled:opacity-40" style={{ background: JACE, color: "#0A0A0A" }} data-testid="jace-generate-report">
                <Sparkles size={13}/> {busy === "generate" ? "GENEREREN…" : "GENERATE REPORT"}
              </button>
            </section>

            {report && (
              <section className="border border-[#F5F3F0]/15 p-5" data-testid="jace-athlete-report">
                <div className="flex items-center justify-between mb-4"><div className="chip" style={{ color: JACE }}>ATHLETE REPORT</div><span className="chip px-2 py-1 border" style={{ color: report.status === "final" ? "#4ADE80" : "#D4A93A", borderColor: "currentColor" }}>{report.status === "final" ? "FINAL" : "DRAFT"}</span></div>
                <div className="space-y-4">
                  {REPORT_SECTIONS.map(([key, label]) => (
                    <div key={key}>
                      <label className="chip text-[#A3A3A3]">{label}</label>
                      <textarea rows={4} value={report.sections?.[key] || ""} onChange={(e) => setReport((p) => ({ ...p, sections: { ...p.sections, [key]: e.target.value }, approved_sections: (p.approved_sections || []).filter((x) => x !== key) }))} className="editorial-input dark w-full" data-testid={`jace-report-${key}`}/>
                      <label className="chip text-[#A3A3A3] inline-flex items-center gap-2 mt-2" data-testid={`jace-approve-${key}`}><input type="checkbox" checked={(report.approved_sections || []).includes(key)} onChange={(e) => setReport((p) => ({ ...p, approved_sections: e.target.checked ? [...(p.approved_sections || []), key] : (p.approved_sections || []).filter((x) => x !== key) }))} className="accent-[#C6FF3C]"/> Goedgekeurd voor delen</label>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2 mt-5">
                  <button onClick={() => saveReport(false)} disabled={!!busy} className="btn-ghost-dark" data-testid="jace-save-report">SAVE</button>
                  <button onClick={() => saveReport(true)} disabled={!!busy} className="chip py-2.5" style={{ background: JACE, color: "#0A0A0A" }} data-testid="jace-finalize-report">FINALIZE</button>
                </div>
                {report.status === "final" && (
                  <div className="grid grid-cols-2 gap-2 mt-4" data-testid="jace-report-actions">
                    <button onClick={downloadPdf} className="chip px-3 py-2 border border-[#F5F3F0]/25 inline-flex items-center justify-center gap-1.5" data-testid="jace-report-pdf"><Download size={12}/> PDF</button>
                    <button onClick={copyReport} className="chip px-3 py-2 border border-[#F5F3F0]/25 inline-flex items-center justify-center gap-1.5" data-testid="jace-report-copy"><Copy size={12}/> COPY</button>
                    <button onClick={() => meta.athlete_phone ? shareWhatsApp() : setShareMode("whatsapp")} className="chip px-3 py-2 border inline-flex items-center justify-center gap-1.5" style={{ borderColor: "#25D366", color: "#25D366" }} data-testid="jace-report-whatsapp"><MessageCircle size={12}/> WHATSAPP</button>
                    <button onClick={() => shareEmail()} className="chip px-3 py-2 border border-[#F5F3F0]/25 inline-flex items-center justify-center gap-1.5" data-testid="jace-report-email"><Mail size={12}/> EMAIL</button>
                    <button onClick={nativeShare} className="chip px-3 py-2 border border-[#F5F3F0]/25 inline-flex items-center justify-center gap-1.5 col-span-2" data-testid="jace-report-share"><Send size={12}/> DEVICE SHARE</button>
                  </div>
                )}
              </section>
            )}
          </aside>
        </div>

        {shareMode && (
          <div className="fixed inset-0 bg-black/75 z-50 flex items-center justify-center p-4" onClick={() => setShareMode(null)}>
            <div className="bg-[#0A0A0A] border border-[#F5F3F0]/20 max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="jace-share-manual">
              <div className="chip mb-2" style={{ color: JACE }}>// HANDMATIG DELEN</div>
              <label className="chip text-[#A3A3A3]">{shareMode === "whatsapp" ? "WhatsApp-nummer" : "E-mailadres"}</label>
              <input value={manualTarget} onChange={(e) => setManualTarget(e.target.value)} className="editorial-input dark w-full" data-testid="jace-share-target"/>
              <div className="flex gap-2 mt-4">
                <button onClick={() => shareMode === "whatsapp" ? shareWhatsApp(manualTarget) : shareEmail(manualTarget)} className="chip px-4 py-2.5" style={{ background: JACE, color: "#0A0A0A" }} data-testid="jace-share-send">DEEL</button>
                <button onClick={() => setShareMode(null)} className="btn-ghost-dark">Annuleer</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </JaceLayout>
  );
}
